<div class="dashboard-layout" *ngIf="activeSession">
  <!-- Left Sidebar: Session Info & Controls -->
  <div class="left-sidebar">
    <div
      class="info-card session-actions-card"
      *ngIf="activeSession.status !== 'completed' && canManageSession"
    >
      <div class="session-actions">
        <button
          *ngIf="canEditSession()"
          (click)="openEditSessionModal()"
          class="btn-action btn-edit-session"
        >
          Edit Session
        </button>
        <button
          *ngIf="activeSession.status === 'active'"
          (click)="endSession()"
          class="btn-action btn-end-session"
        >
          End Session
        </button>
      </div>
    </div>

    <div
      class="info-card session-actions-card"
      *ngIf="!isAuthenticated && activeSession.status === 'active'"
    >
      <div class="session-actions">
        <button
          (click)="loginToJoinSession()"
          class="btn-action btn-edit-session"
        >
          Login to Join Session
        </button>
      </div>
    </div>

    <div class="info-card sessionid-info">
      <div class="info-row" (click)="copyMessage(activeSession.id)">
        <div class="info-content">
          <div class="info-label">Session ID</div>
          <div class="info-value">
            {{ activeSession.id || "N/A" }}
          </div>
        </div>
      </div>
      <div *ngIf="showSuccessMessage" class="message message-success">
        {{ successMessage }}
      </div>
    </div>
    <!-- Track & Car Info Card -->
    <div class="info-card track-info">
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Status</div>
          <div class="info-value">
            <span class="status-badge" [ngClass]="sessionStateClass">
              {{ sessionStateLabel }}
            </span>
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Session</div>
          <div class="info-value">
            {{ activeSession.name }}
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Joinability</div>
          <div class="info-value">
            <span
              class="visibility-badge"
              [class.public]="sessionVisibilityClass === 'public'"
              [class.private]="sessionVisibilityClass === 'private'"
            >
              {{ sessionVisibilityLabel }}
            </span>
          </div>
        </div>
      </div>
      <div class="info-row" *ngIf="hasTeamBranding">
        <div class="info-content">
          <div class="info-label">Team</div>
          <div class="info-value team-branding-value">
            <span class="team-badge-avatar">
              <img
                *ngIf="teamLogoUrl; else currentSessionTeamInitial"
                [src]="teamLogoUrl"
                alt="Team logo"
              />
              <ng-template #currentSessionTeamInitial>
                <span>{{ teamInitial }}</span>
              </ng-template>
            </span>
            <span class="team-badge-name">{{ teamDisplayName }}</span>
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Start</div>
          <div class="info-value">
            {{ formatSessionDate(activeSession.sessionDetails.startTime) }}
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">End</div>
          <div class="info-value">
            {{ formatSessionDate(activeSession.sessionDetails.endTime) }}
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Track</div>
          <div class="info-value">
            {{ activeSession.sessionDetails.trackName || "N/A" }}
          </div>
        </div>
      </div>
      <div class="info-row">
        <div class="info-content">
          <div class="info-label">Allowed Cars</div>
          <div class="info-value">
            {{ activeSession.allowedCarClasses?.join(", ") }}
          </div>
        </div>
      </div>
    </div>

    <!-- Session Stats Card -->
    <div class="info-card session-stats-card">
      <div class="card-header">
        <h3>Session Stats</h3>
        <span class="status-badge" [ngClass]="sessionStateClass">
          ● {{ sessionStateLabel }}
        </span>
      </div>

      <div class="stats-container">
        <!-- Laps Progress -->
        <div class="stat-block primary">
          <div class="stat-details">
            <div class="stat-label">Total Laps Remaining</div>
            <div class="stat-value large">{{ remainingLaps }}</div>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <div class="stat-block secondary">
            <div class="stat-label">Stints Completed</div>
            <div class="stat-value">
              {{ stintsCompleted }}
            </div>
          </div>
          <div class="stat-block secondary" *ngIf="activeSession.bestLap">
            <div class="stat-label">Best Lap</div>
            <div class="stat-value best-lap">{{ getBestLapTime() }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Center: Main Lap Times Table -->
  <div class="center-content">
    <div class="content-header">
      <h2>
        {{ isSessionScheduled ? "Session Status" : "All Lap Times" }}
      </h2>
      <div class="lap-count" *ngIf="!isSessionScheduled">
        {{ displayedLaps.length }}
        {{ displayedLaps.length === 1 ? "lap" : "laps" }}
      </div>
    </div>

    <div
      *ngIf="isSessionScheduled || hasSessionConcluded"
      class="scheduled-message"
    >
      <span class="scheduled-badge">{{
        hasSessionConcluded ? "Completed" : "Scheduled"
      }}</span>
      <p class="scheduled-title">
        {{
          hasSessionConcluded
            ? "This session has concluded"
            : "This session is not live yet"
        }}
      </p>
      <p class="scheduled-time" *ngIf="!hasSessionConcluded">
        Starts: {{ formatSessionDate(activeSession.sessionDetails.startTime) }}
      </p>
      <p
        class="scheduled-time"
        *ngIf="activeSession.sessionDetails.endTime && !hasSessionConcluded"
      >
        Ends: {{ formatSessionDate(activeSession.sessionDetails.endTime) }}
      </p>
      <p
        class="scheduled-time"
        *ngIf="activeSession.sessionDetails.endTime && hasSessionConcluded"
      >
        Ended: {{ formatSessionDate(activeSession.sessionDetails.endTime) }}
      </p>
      <p class="hint" *ngIf="!hasSessionConcluded">
        Lap tracking starts automatically when the session goes live.
      </p>
    </div>

    <div
      *ngIf="!isSessionScheduled && activeSession.lapsCompleted === 0"
      class="waiting-message"
    >
      <div class="spinner"></div>
      <p class="waiting-title">Waiting for lap data...</p>
      <div class="telemetry-instructions-card">
        <p class="hint">To send telemetry, follow these steps:</p>
        <ol class="telemetry-steps">
          <li>
            Start Telemetry Client, log in, and join the session (copy the
            Session ID in the top-left corner:
            <code>{{ sessionJoinId }}</code
            >).
          </li>
          <li>Start iRacing.</li>
          <li>
            Start Test Drive in iRacing with
            <strong>{{ telemetryAllowedCars }}</strong> on Track:
            <strong>{{ telemetryTrackName }}</strong
            >.
          </li>
          <li>
            Drive your stint (<strong>{{ telemetryLapsPerDriver }}</strong>
            {{ telemetryLapsPerDriver === 1 ? "Lap" : "Laps" }}).
          </li>
        </ol>
      </div>
    </div>

    <div *ngIf="displayedLaps.length > 0" class="laps-table-container">
      <table class="laps-table">
        <thead>
          <tr>
            <th class="lap-num">Lap</th>
            <th class="driver-col">Driver</th>
            <th class="sector">Sectors</th>
            <th class="delta-col">Delta</th>
            <th class="lap-time">Time</th>
            <th class="badge-col"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let lap of displayedLaps; let i = index"
            [class.best-lap-row]="isBestLap(lap)"
            [class.latest-lap]="i === displayedLaps.length - 1"
            [class.invalid-lap-row]="lap.isValid === false"
          >
            <td class="lap-num">
              <span class="lap-num-content">
                <span>{{ getDisplayLapNumber(lap) }}</span>
              </span>
            </td>
            <td class="driver-col">{{ getLapDriverName(lap) }}</td>
            <td>
              <span class="sector-cluster">
                <span
                  *ngFor="let sectorIndex of displayedSectorIndexes"
                  class="sector-chip"
                  [ngClass]="getSectorChipClass(lap, sectorIndex)"
                  [attr.aria-label]="getSectorTooltip(lap, sectorIndex)"
                  tabindex="0"
                >
                  <span class="sector-popover">{{
                    getSectorTooltip(lap, sectorIndex)
                  }}</span>
                </span>
              </span>
            </td>
            <td>
              <span *ngIf="isNotBestLap(lap)" class="delta">
                +{{ formatDelta(getDeltaTime(lap)) }}
              </span>
            </td>
            <td>
              <span class="time-value">{{ formatTime(lap.lapTime) }}</span>
            </td>
            <td class="badge-col">
              <div class="badge-stack">
                <span
                  *ngIf="isBestLap(lap) && lap.isValid !== false"
                  class="best-badge"
                  >Best</span
                >
                <span
                  *ngIf="i === displayedLaps.length - 1"
                  class="latest-badge"
                >
                  Latest
                </span>
                <span
                  *ngIf="lap.isValid === false"
                  class="status-badge invalid-badge"
                >
                  Invalid
                  <div class="tooltip-container">
                    <span class="tooltip-text">
                      <strong>Lap {{ lap.lapNumber }} Invalid</strong>
                      <br />
                      {{ lap.invalidReason || "No specific reason provided" }}
                    </span>
                  </div>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Right Sidebar: Mini Leaderboard -->
  <div class="right-sidebar">
    <div class="info-card leaderboard-card">
      <div class="card-header">
        <h3>
          <span *ngIf="activeSession.status === 'completed'">Final</span
          ><span *ngIf="activeSession.status !== 'completed'">Current</span>
          Standings
        </h3>
      </div>
      <app-mini-leaderboard
        [session]="activeSession"
        [selectedDriverIds]="selectedDriverIds"
        (selectedDriverIdsChange)="onSelectedDriverIdsChange($event)"
      ></app-mini-leaderboard>
    </div>
  </div>

  <div class="info-card activity-log-panel">
    <div class="card-header activity-log-header">
      <h3>Session Log</h3>
      <span class="activity-log-count">
        {{ sessionActivities.length }}
        {{ sessionActivities.length === 1 ? "line" : "lines" }}
      </span>
    </div>
    <div
      class="activity-log-shell"
      #activityLogScroller
      role="log"
      aria-live="polite"
    >
      <p *ngIf="sessionActivities.length === 0" class="activity-log-line muted">
        <span class="activity-log-time">[--:--:--]</span>
        <span class="activity-log-tag">[INFO]</span>
        Waiting for activity...
      </p>
      <p
        *ngFor="let activity of sessionActivities; trackBy: trackByActivityId"
        class="activity-log-line"
        [ngClass]="getActivityLevelClass(activity)"
      >
        <span class="activity-log-time"
          >[{{ formatActivityTime(activity.createdAt) }}]</span
        >
        <span class="activity-log-tag"
          >[{{ getActivityCategoryTag(activity) }}]</span
        >
        <span>{{ activity.message }}</span>
      </p>
    </div>
  </div>
</div>

<app-edit-session-modal
  *ngIf="showEditSessionModal && activeSession"
  [session]="activeSession"
  (close)="closeEditSessionModal()"
  (sessionUpdated)="onSessionUpdated()"
></app-edit-session-modal>
